'use client';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { interpolate } from '@format/interpolate';
import { Icon } from '@ui/icons';
import { Modal } from '@web/components/Modal';
import { VideoModal } from '@web/components/VideoModal';
import { useReporter } from '../../../reporter';
import { useShopifyOfferContext } from '../../../offer/shopifyContext';
import { useCarousel } from '../../../hooks';
import { rotatingPromoBarClicked } from './events';
import {
  StyledRotatingPromoBar,
  MessageWrapper,
  PromoIcon,
  PromoLink,
  PromoLinkButton,
  PromoLinkText,
  PromoLinkChevron,
  PromoViewport,
  ArrowButton,
} from './styles';

export const RotatingPromoBar = ({
  messages = [],
  backgroundColor = 'background.dark',
  fontColor = 'text.lightest',
  sticky = false,
  asCarousel = false,
  carouselConfig = null,
}) => {
  const [showModal, setShowModal] = useState(false);
  const { formattedAmount } = useShopifyOfferContext();
  const reporter = useReporter();

  const {
    activeIndex,
    outgoingIndex,
    direction,
    goNext,
    goPrev,
    animationConfig,
    arrowsVisible,
    handlers,
  } = useCarousel({
    count: messages.length,
    autoplayEnabled: asCarousel && (carouselConfig?.autoplayEnabled ?? true),
    autoplayIntervalMs: carouselConfig?.autoplayIntervalMs ?? 4000,
    transitionDurationMs: carouselConfig?.transitionDurationMs ?? 500,
    transitionEasing:
      carouselConfig?.transitionEasing ?? 'cubic-bezier(0.4, 0, 0.2, 1)',
    resumeAfterMs: carouselConfig?.resumeAfterMs ?? 8000,
    pauseOnHover: carouselConfig?.pauseOnHover ?? true,
    showArrowControls: carouselConfig?.showArrowControls ?? true,
  });

  // Reset modal state when the active message changes.
  useEffect(() => {
    setShowModal(false);
  }, [activeIndex]);

  if (!messages.length) return null;

  const {
    linkText,
    linkTarget,
    triggerModal = false,
    modal,
  } = messages[activeIndex];
  const interpolatedLinkText = linkText
    ? interpolate(linkText, { offer: formattedAmount })
    : null;

  const handleLinkClick = () => {
    reporter.tag(rotatingPromoBarClicked(interpolatedLinkText, linkTarget));
    if (triggerModal) setShowModal(true);
  };

  const modalNode =
    showModal && modal ? (
      modal.component === 'video_modal' ? (
        <VideoModal shown onClickClose={() => setShowModal(false)} {...modal} />
      ) : (
        <Modal shown onCloseModal={() => setShowModal(false)} {...modal} />
      )
    ) : null;

  const renderSlide = (msgIndex, isActive, wrapperProps) => {
    const msg = messages[msgIndex];
    const {
      message: m,
      icon: ic,
      iconAlignment: ia = 'left',
      linkText: lt,
      linkTarget: ltar,
      linkStyle: ls = 'link',
      triggerModal: tm = false,
    } = msg;
    const interp = lt ? interpolate(lt, { offer: formattedAmount }) : null;
    const icNode = ic ? (
      <PromoIcon $src={ic.src} role="img" aria-label={ic.alt} />
    ) : null;
    const onLinkClick = isActive ? handleLinkClick : () => {};
    const lnk = interp ? (
      tm ? (
        <PromoLinkButton
          $isButton={ls === 'button'}
          $fontColor={fontColor}
          onClick={onLinkClick}
        >
          <PromoLinkText>{interp}</PromoLinkText>
          {ls !== 'button' && (
            <PromoLinkChevron aria-hidden="true">›</PromoLinkChevron>
          )}
        </PromoLinkButton>
      ) : (
        <PromoLink
          $isButton={ls === 'button'}
          $fontColor={fontColor}
          href={ltar}
          onClick={onLinkClick}
        >
          <PromoLinkText>{interp}</PromoLinkText>
          {ls !== 'button' && (
            <PromoLinkChevron aria-hidden="true">›</PromoLinkChevron>
          )}
        </PromoLink>
      )
    ) : null;
    return (
      <MessageWrapper {...wrapperProps}>
        {ia === 'left' && icNode}
        {m}
        {ia === 'between' && icNode}
        {lnk}
        {ia === 'right' && icNode}
      </MessageWrapper>
    );
  };

  return (
    <StyledRotatingPromoBar
      data-testid="rotating-promo-bar"
      $backgroundColor={backgroundColor}
      $fontColor={fontColor}
      $sticky={sticky}
      {...handlers}
    >
      {arrowsVisible && (
        <ArrowButton
          $direction="prev"
          onClick={goPrev}
          aria-label="Previous message"
        >
          <Icon
            iconName="arrow-left"
            alt=""
            size="22px"
            color={fontColor}
            id="promo-bar-prev-arrow"
          />
        </ArrowButton>
      )}
      <PromoViewport>
        {outgoingIndex !== null ? (
          <>
            {renderSlide(activeIndex, true, {
              key: `in-${activeIndex}`,
              $entering: true,
              $direction: direction,
              $animation: animationConfig,
            })}
            {renderSlide(outgoingIndex, false, {
              key: `out-${outgoingIndex}`,
              $exiting: true,
              $direction: direction,
              $animation: animationConfig,
            })}
          </>
        ) : (
          renderSlide(activeIndex, true, { key: `static-${activeIndex}` })
        )}
      </PromoViewport>
      {arrowsVisible && (
        <ArrowButton
          $direction="next"
          onClick={goNext}
          aria-label="Next message"
        >
          <Icon
            iconName="arrow-forward"
            alt=""
            size="22px"
            color={fontColor}
            id="promo-bar-next-arrow"
          />
        </ArrowButton>
      )}
      {modalNode}
    </StyledRotatingPromoBar>
  );
};

RotatingPromoBar.displayName = 'RotatingPromoBar';

RotatingPromoBar.propTypes = {
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      message: PropTypes.node,
      icon: PropTypes.shape({ src: PropTypes.string, alt: PropTypes.string }),
      iconAlignment: PropTypes.oneOf(['left', 'between', 'right']),
      linkText: PropTypes.string,
      linkTarget: PropTypes.string,
      linkStyle: PropTypes.oneOf(['link', 'button']),
      triggerModal: PropTypes.bool,
      modal: PropTypes.object,
    }),
  ),
  backgroundColor: PropTypes.string,
  fontColor: PropTypes.string,
  sticky: PropTypes.bool,
  asCarousel: PropTypes.bool,
  carouselConfig: PropTypes.shape({
    autoplayEnabled: PropTypes.bool,
    autoplayIntervalMs: PropTypes.number,
    transitionDurationMs: PropTypes.number,
    resumeAfterMs: PropTypes.number,
    pauseOnHover: PropTypes.bool,
    showArrowControls: PropTypes.bool,
  }),
};
